import { logoPrincipal } from '../assets';
import { useEffect, useRef, useState } from 'react';
import useFirestoreCollection from '../hooks/useFirestoreCollection';

import fotoBoton from '../assets/images/Foto_boton.jpeg';
import ahorrandoNaty from '../assets/images/ahorrandoNaty.png';
import automatizaPro from '../assets/images/automatizaPro.png';
import dealFlow from '../assets/images/dealFlow.png';
import ImperioDigital from '../assets/images/ImperioDigital.png';
import SmartSavingClub from '../assets/images/SmartSavingClub.png';
import SEAbyte_logo from '../assets/images/SEAbyte_logo.png';
import DiamondLogo from '../assets/images/Logo_Diamond.png';

const LetterByLetterText = ({ text, className = '', startIndex = 0 }) => {
  let charIndex = 0;

  return (
    <span className={className}>
      {text.split('').map(() => {
        const globalIndex = startIndex + charIndex;
        const letter = text[charIndex];
        charIndex++;

        return (
          <span
            key={globalIndex}
            className="letter-draw"
            style={{
              animationDelay: `${globalIndex * 0.08}s`,
              whiteSpace: letter === ' ' ? 'pre' : 'normal',
            }}
          >
            {letter}
          </span>
        );
      })}
    </span>
  );
};

export default function Hero() {
  const [cursor, setCursor] = useState({ x: 0, y: 0, visible: false });
  const carouselRef = useRef(null);

  const [inView, setInView] = useState(false);
  const heroRef = useRef(null);

  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    const updateFromPoint = (clientX, clientY) => {
      const rect = el.getBoundingClientRect();
      setCursor({
        x: clientX - rect.left,
        y: clientY - rect.top,
        visible: true,
      });
    };

    const onMove = (e) => updateFromPoint(e.clientX, e.clientY);
    const onLeave = () => setCursor((c) => ({ ...c, visible: false }));

    const onTouchStart = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      updateFromPoint(t.clientX, t.clientY);
    };
    const onTouchMove = (e) => {
      const t = e.touches?.[0];
      if (!t) return;
      updateFromPoint(t.clientX, t.clientY);
    };
    const onTouchEnd = () => setCursor((c) => ({ ...c, visible: false }));

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);

    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  const defaultClientLogos = [
    { name: 'Ahorrando Naty', src: ahorrandoNaty },
    { name: 'Automatiza Pro', src: automatizaPro },
    { name: 'Deal Flow', src: dealFlow },
    { name: 'Imperio Digital', src: ImperioDigital },
    { name: 'Smart Saving Club', src: SmartSavingClub },
    { name: 'SEAbyte', src: SEAbyte_logo },
    { name: 'Diamond Academy', src: DiamondLogo },
  ];

  const { data: clients } = useFirestoreCollection('clients');
  const clientItems = clients.length > 0 ? clients : [];
  const loopItems = [...clientItems, ...clientItems];
  const getLogoHref = (logo) => logo.link || logo.href || '#';

  const baseDelay = 300;
  const ease = 'ease-[cubic-bezier(0.16,1,0.3,1)]';

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex flex-col pt-32 overflow-hidden bg-[#EEF2F7] dark:bg-slate-950"
    >
      <style>{`
        @keyframes slide {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
      {/* ✅ Background video (cubre TODA la sección) */}
      <div className="absolute inset-0 z-0 overflow-hidden">
          <iframe
            className="
              absolute left-1/2 top-1/2
              -translate-x-1/2 -translate-y-1/2
              pointer-events-none

              w-[260%] h-[260%] rotate-90
              sm:w-[170%] sm:h-[170%] sm:rotate-0
              md:w-[145%] md:h-[145%]
              lg:w-[140%] lg:h-[140%]
            "
            src="https://www.youtube.com/embed/NbJRcvX6ygE?autoplay=1&mute=1&controls=0&loop=1&playlist=NbJRcvX6ygE&vq=hd1080"
            title="Hero background video"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />


        {/* ✅ overlays para legibilidad */}
        <div className="absolute inset-0 bg-black/55 dark:bg-black/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-white/10 to-transparent dark:from-slate-950/70 dark:via-slate-950/45 dark:to-slate-950/25" />

        <div className="absolute -top-28 -left-28 w-[520px] h-[520px] bg-lime-400/10 blur-[150px] rounded-full" />
        <div className="absolute -bottom-28 left-[30%] w-[520px] h-[620px] bg-[#3662E2]/12 blur-[160px] rounded-full" />
        <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply dark:mix-blend-soft-light bg-[url('https://www.transparenttextures.com/patterns/asfalt-light.png')]" />
      </div>

      {/* ✅ Contenido encima del background */}
      <div className="relative z-10 w-full flex-1 flex flex-col">
        {/* Layout centrado */}
        <div className="max-w-5xl mx-auto px-6 relative w-full flex-1 flex items-center">
          <div className="w-full text-center">
            
            <h1
              className={[
                'text-5xl md:text-[6.5rem] font-display font-black leading-[1.1] mb-10 mt-16 tracking-tighter',
                'text-slate-200',
                'drop-shadow-[0_14px_40px_rgba(0,0,0,0.85)] dark:drop-shadow-sm',
                'transform-gpu transition-all duration-[1100ms]',
                ease,
                inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6',
              ].join(' ')}
              style={{ transitionDelay: `${baseDelay + 400}ms` }}
            >
              <LetterByLetterText text="CONSTRUIMOS" startIndex={0} />
              <br />
              <LetterByLetterText text="MARCAS" startIndex={11} />
              <br />
              <span className="italic">
                <LetterByLetterText text="QUE DOMINAN" startIndex={17} />
              </span>
            </h1>

            <p
              className={[
                'text-xl md:text-2xl text-white/95 dark:text-slate-200',
                'drop-shadow-[0_10px_26px_rgba(0,0,0,0.8)]',
                'mb-12 max-w-3xl mx-auto leading-relaxed font-light tracking-wide',
                'transform-gpu transition-all duration-[1000ms]',
                ease,
                inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6',
              ].join(' ')}
              style={{ transitionDelay: `${baseDelay + 1000}ms` }}
            >
              Somos el aliado que necesitas. En Next Global Media nos enfocamos en darte resultados.
            </p>

            <div
              className={[
                'flex flex-col sm:flex-row gap-6 justify-center',
                'transform-gpu transition-all duration-[1000ms]',
                ease,
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
              ].join(' ')}
              style={{ transitionDelay: `${baseDelay + 1500}ms` }}
            >
              {/* ✅ BOTÓN: ahora con fondo blanco - COMENTADO PARA FUTURA ACTIVACIÓN */}
              {/* <a
                className="
                  relative
                  flex items-center justify-center
                  px-20 py-7 rounded-xl
                  transition-all hover:scale-[1.32]
                  shadow-[0_18px_55px_rgba(0,0,0,0.35)] hover:shadow-[0_22px_70px_rgba(0,0,0,0.45)]
                  bg-white
                "
                href="#process"
                aria-label="Ahorrando con Naty"
                title="Ahorrando con Naty"
                style={{
                  backgroundImage: `url(${fotoBoton})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                }}
              /> */}

              <a
                className="
                  flex items-center justify-center gap-4
                  bg-teal-400 hover:bg-teal-500 text-slate-950
                  px-20 py-7 rounded-xl font-bold text-lg
                  transition-all hover:scale-[1.12]
                  shadow-[0_18px_55px_rgba(0,0,0,0.05)] hover:shadow-[0_22px_70px_rgba(0,0,0,0.45)]
                  group
                "
                href="#contact"
              >
                Cuéntanos tu proyecto
                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform text-sm">
                  arrow_forward_ios
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <div
        className={[
          'mt-100 flex justify-center',
          'transform-gpu transition-all duration-[900ms]',
          ease,
          inView ? ' translate-y-0' : ' translate-y-4',
        ].join(' ')}
        style={{ transitionDelay: `${baseDelay + 2000}ms` }}
      >
        <h3 className="text-3xl md:text-5xl font-display font-bold text-white hover:text-white transition-all duration-500 text-center my-8">
          Empresas y proyectos <br /> que confían en Next Global Media.
          
        </h3>
      </div>
      <div
        className={[
          'mt-8',
          'w-full relative z-10 pb-14',
          'transform-gpu transition-all duration-[1100ms]',
          ease,
          inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6',
        ].join(' ')}
        style={{ transitionDelay: `${baseDelay + 2500}ms` }}
      >
        <div className="max-w-6xl mx-auto px-6">
          <div ref={carouselRef} className="hero-logo-slider relative h-[210px] overflow-hidden bg-transparent border border-slate-300/30 shadow-[0_20px_80px_rgba(15,23,42,0.08)] rounded-[2rem]">
            <div
              className="pointer-events-none absolute z-30 transition-opacity duration-150"
              style={{
                opacity: cursor.visible ? 1 : 0,
                left: cursor.x,
                top: cursor.y,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <span className="block w-3 h-3 rounded-full bg-emerald-400 shadow-[0_10px_20px_rgba(16,185,129,0.35)]" />
            </div>
            <div className="py-3">
              <div className="hero-logo-slider-track inline-flex items-center gap-4" style={{ animation: 'slide 20s linear infinite' }}>
                {loopItems.map((logo, idx) => (
                    <a
                      key={`${logo.name}-${idx}`}
                      href={getLogoHref(logo)}
                      target={getLogoHref(logo) !== '#' ? '_blank' : undefined}
                      rel={getLogoHref(logo) !== '#' ? 'noopener noreferrer' : undefined}
                      title={logo.name}
                     className="group relative flex-shrink-0 h-auto min-h-[160px] min-w-[190px] 
                                rounded-[2rem] 
                                bg-gradient-to-br from-slate-200/90 via-slate-300/80 to-slate-200/90 
                                dark:from-slate-700/70 dark:via-slate-800/70 dark:to-slate-700/70
                                border border-slate-400/40 dark:border-slate-600/40
                                shadow-lg backdrop-blur-xl 
                                p-5 flex flex-col items-center justify-center overflow-hidden 
                                transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
                    >
                    <div className="relative flex h-24 w-full items-center justify-center rounded-[1.75rem] bg-slate-100/80 border border-slate-300/70 p-4 shadow-[inset_0_0_18px_rgba(255,255,255,0.55)]">
                      <img
                        src={logo.logo || logo.src}
                        alt={logo.name}
                        className="max-h-16 max-w-full object-contain"
                        loading="lazy"
                      />
                    </div>

                    <span className="mt-4 flex flex-col leading-tight items-center text-center">
                      <span className="text-sm font-black text-slate-950 group-hover:text-cyan-700 transition-colors">
                        {logo.name}
                      </span>
                    </span>

                    <span className="pointer-events-none absolute inset-0 rounded-[2rem] bg-gradient-to-b from-white/25 via-transparent to-transparent opacity-60" />
                  </a>
                ))}
              </div>
            </div>
            <p className="mt-3 text-center text-xs text-white/80 dark:text-slate-400 drop-shadow-[0_8px_18px_rgba(0,0,0,0.75)]">
              Marcas que dominan.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}